import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Check, Loader2, Mail } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactModal = ({ isOpen, onClose }) => {
  const { t, language } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    companyName: '',
    message: '',
    phone: ''
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = t('contact.errors.nameRequired');
    if (!formData.email.trim()) newErrors.email = t('contact.errors.emailRequired');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('contact.errors.emailInvalid');
    }
    if (!formData.companyName.trim()) newErrors.companyName = t('contact.errors.companyRequired');
    if (!formData.message.trim()) newErrors.message = t('contact.errors.messageRequired');
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        company_name: formData.companyName,
        message: formData.message,
        phone: formData.phone || null,
        language: language // Send language for localized emails
      });
      
      if (response.data) {
        setSubmitSuccess(true);
      }
    } catch (error) {
      
      alert('Failed to send message. Please try again.');
    }
    setIsSubmitting(false);
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      companyName: '',
      message: '',
      phone: ''
    });
    setErrors({});
    setSubmitSuccess(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#0d1442]">
            {submitSuccess ? t('contact.successTitle') : t('contact.title')}
          </DialogTitle>
        </DialogHeader>

        {!submitSuccess ? (
          <div className="space-y-4 mt-4">
            {/* Contact Form */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">{t('contact.fullName')} *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="John Doe"
                  className={errors.name ? 'border-red-500' : ''}
                  data-testid="contact-name-input"
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>

              <div>
                <Label htmlFor="email">{t('contact.emailAddress')} *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="john@company.com"
                  className={errors.email ? 'border-red-500' : ''}
                  data-testid="contact-email-input"
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>

              <div>
                <Label htmlFor="companyName">{t('contact.companyName')} *</Label>
                <Input
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  placeholder="Your Company"
                  className={errors.companyName ? 'border-red-500' : ''}
                  data-testid="contact-company-input"
                />
                {errors.companyName && <p className="text-red-500 text-xs mt-1">{errors.companyName}</p>}
              </div>

              <div>
                <Label htmlFor="phone">{t('contact.phone')}</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+421 XXX XXX XXX"
                  data-testid="contact-phone-input"
                />
              </div>

              <div>
                <Label htmlFor="message">{t('contact.message')} *</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder={t('contact.messagePlaceholder')}
                  rows={4}
                  className={errors.message ? 'border-red-500' : ''}
                  data-testid="contact-message-input"
                />
                {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <Button 
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="btn-gradient text-white px-8 py-3 rounded-lg"
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('contact.sending')}
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    {t('contact.send')}
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          /* Success State */
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-[#0d1442] mb-2">
              {t('contact.messageSent')}
            </h3>
            <p className="text-slate-600 mb-6">
              {t('contact.confirmationText')}
            </p>
            <p className="text-sm text-slate-500 mb-6">
              {t('contact.confirmationEmail')} {formData.email}
            </p>
            <Button 
              onClick={handleClose}
              className="btn-gradient text-white px-8 py-3 rounded-lg"
              data-testid="contact-close-btn"
            >
              {t('contact.close')}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ContactModal;
